import React, { useState, useEffect } from 'react';
import { DB } from '../data/database';
import { useLanguage, LANGUAGE_OPTIONS } from '../translations/LanguageContext';

const PUBLIC_SIGNUP_ENABLED = false;
const SAVED_CREDS_KEY = 'bigstar_saved_login';
const DARK = '#14161C';
const PAPER = '#FAFAF8';
const BLUE = '#2563EB';
const INK = '#14161C';
const INK3 = '#8B8D94';
const LINE = '#E5E5E3';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

function getLogoUrl() {
  try {
    const cfg = DB.get('appConfig');
    return (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) ? cfg.company_info?.logo_url || '' : '';
  } catch { return ''; }
}

function Login({ onLogin, activeLangs = ['en','ar','tr','ru','ka','fr'], timedOut = false, onBackToWebsite }) {
  const { t, lang, changeLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const logoUrl = getLogoUrl();

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SAVED_CREDS_KEY));
      if (saved) {
        setEmail(saved.email || '');
        setPassword(saved.password || '');
        setRemember(true);
      }
    } catch {}
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const input = email.trim();
    const pw = password;

    if (remember) {
      localStorage.setItem(SAVED_CREDS_KEY, JSON.stringify({ email: input, password: pw }));
    } else {
      localStorage.removeItem(SAVED_CREDS_KEY);
    }

    const users = DB.get('users') || [];
    const staffUser = users.find(u =>
      (u.email === input || u.username === input || u.name === input) && u.password === pw
    );

    if (staffUser) {
      if (staffUser.frozen) { setError(t('login_blocked')); return; }
      if (staffUser.preferredLanguage) changeLanguage(staffUser.preferredLanguage);
      onLogin({ ...staffUser, type: 'staff' });
      return;
    }

    const customers = DB.get('customers') || [];
    const customer = customers.find(c =>
      (c.email === input || c.username === input || c.name === input) && c.password === pw
    );

    if (customer) {
      if (customer.blocked || customer.frozen) { setError(t('login_blocked')); return; }
      if (customer.account_status === 'pending_verification') { setError('Your account is under review. We\'ll notify you once approved.'); return; }
      if (customer.account_status === 'rejected') { setError('Your application was not approved. Contact us for details.'); return; }
      if (customer.account_status === 'suspended') { setError('Your account has been suspended. Contact us.'); return; }
      const session = { time: new Date().toISOString(), device: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop' };
      const sessions = [...(customer.loginSessions || []), session].slice(-20);
      const updated = { ...customer, loginSessions: sessions };
      DB.set('customers', customers.map(c => c.id === customer.id ? updated : c));
      if (customer.preferredLanguage) changeLanguage(customer.preferredLanguage);
      onLogin({ ...updated, type: 'customer', role: 'customer' });
      return;
    }

    setError(t('login_invalid'));
  };

  const inputStyle = {
    width: '100%', height: 48, padding: '0 16px', borderRadius: 10,
    border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading,
    fontSize: 15, color: INK, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: heading, background: PAPER, padding: '48px 24px 80px', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ display: 'flex', width: '100%', maxWidth: 900, borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: `1px solid ${LINE}` }} className="ws-login-card">
      {/* ── Left panel (dark) ── */}
      <div style={{ width: '44%', minWidth: 0, background: DARK, color: PAPER, padding: '40px 36px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }} className="ws-login-left">
        {/* Grid bg */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none', backgroundImage: `linear-gradient(rgba(250,250,248,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,248,0.5) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo */}
          <div style={{ marginBottom: 48 }}>
            {logoUrl ? (
              <img src={logoUrl} alt="" style={{ height: 32, objectFit: 'contain', filter: 'brightness(10)' }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg viewBox="0 0 28 28" style={{ width: 28, height: 28 }}>
                  <rect width="28" height="28" rx="6" fill="#fff"/>
                  <text x="14" y="20" textAnchor="middle" fill={DARK} fontSize="18" fontWeight="bold" fontFamily="system-ui">★</text>
                </svg>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>BIGSTAR</div>
                  <div style={{ fontFamily: mono, fontSize: 8, letterSpacing: '0.12em', color: 'rgba(250,250,248,0.35)', textTransform: 'uppercase' }}>UAE · Est. 2008</div>
                </div>
              </div>
            )}
          </div>

          {/* Welcome text */}
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.35)', marginBottom: 16 }}>Welcome Back</div>
          <h1 style={{ fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: 32 }}>
            Your saved cars,<br />
            your inquiries —<br />
            all in one place.
          </h1>

          {/* Benefits */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Track inquiry status in real-time',
              'Saved vehicles & arrival alerts',
              'Trade-verified accounts (registered dealers)',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(250,250,248,0.5)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="rgba(250,250,248,0.3)" strokeWidth="2" style={{ width: 16, height: 16, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom security note */}
        <div style={{ position: 'relative', zIndex: 1, fontFamily: mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(250,250,248,0.15)', marginTop: 48 }}>
          Secured · SSL/TLS · UAE Data Residency
        </div>
      </div>

      {/* ── Right panel (light) ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <h2 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.03em', color: INK, marginBottom: 8 }}>Sign in</h2>
          {PUBLIC_SIGNUP_ENABLED && (
            <div style={{ fontSize: 14, color: INK3, marginBottom: 32 }}>
              New here?{' '}
              <span onClick={() => window.history.replaceState({}, '', '?page=website-register') || window.location.reload()} style={{ color: BLUE, cursor: 'pointer', textDecoration: 'underline' }}>Create an account</span> →
            </div>
          )}

          {/* Session timeout */}
          {timedOut && (
            <div style={{ background: 'rgba(210,153,34,0.08)', border: '1px solid #d29922', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#d29922', fontSize: 13, textAlign: 'center' }}>
              {t('login_timeout')}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#DC2626', fontSize: 13, textAlign: 'center' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="on">
            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' }}>Username</label>
              <input type="text" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="" autoComplete="username" required
                style={inputStyle} />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: INK }}>Password</label>
                <span style={{ fontSize: 12, color: BLUE, cursor: 'pointer' }}>Forgot?</span>
              </div>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" required
                style={inputStyle} />
            </div>

            {/* Remember me */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <input type="checkbox" id="remember" checked={remember}
                onChange={e => setRemember(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: BLUE, borderRadius: 4 }} />
              <label htmlFor="remember" style={{ fontSize: 13, color: INK3, cursor: 'pointer' }}>
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Submit */}
            <button type="submit" style={{
              width: '100%', height: 52, borderRadius: 12, background: INK, color: '#fff',
              fontFamily: heading, fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              Sign in <span style={{ fontSize: 16 }}>→</span>
            </button>
          </form>
        </div>
      </div>

      {/* ── Responsive ── */}
      <style>{`
        @media (max-width: 768px) {
          .ws-login-left { display: none !important; }
          .ws-login-card { max-width: 440px !important; }
        }
        .ws-login-card input:focus { border-color: #2563EB !important; }
        .ws-login-card button[type="submit"]:hover { background: #2A2D35 !important; }
      `}</style>
      </div>{/* close ws-login-card */}
    </div>
  );
}

export default Login;
